<template>
  <div id="costed-bom" class="content">
    <h1><Icon type="ios-analytics" /> {{ pageTitle }}</h1>
    <div class="content-body table-wrapper">
      <div class="block">
        <div class="text-align-right">
          <Button data-test-id="purchased-part-button" class="float-right" type="primary" @click="handleToMaterial">{{ $t('cost.costed_bom.add_purchased_parts_cost') }}</Button>
        </div>
        <div data-test-id="cost-estimator-info">
          <div class="qty-indicator" v-if="costEstimator">
            <router-link :to="{ name: `menu.${costEstimator.manufacturer_part.component_type}.edit`, params: { partId: costEstimator.manufacturer_part.id } }" target="_blank">
              <span class="qty-indicator--number" style="color: #2D8cF0">
                {{ costEstimator.manufacturer_part ? costEstimator.manufacturer_part.chinese_name : '' }}
              </span>
            </router-link>
            &nbsp;
            <span class="qty-indicator--title">{{ $t('cost.part_cost_form.quantity') }}</span>
            <span class="qty-indicator--number">{{ costEstimator.quantity }}</span>
            &nbsp;
            <span class="qty-indicator--title">{{ $t('cost.part_cost_form.currency') }}</span>
            <span class="qty-indicator--number">{{ costEstimator.root_assembly_part_cost.currency }}</span>
            &nbsp;
            <span class="qty-indicator--title">{{ $t('exchange_rate') }}</span>
            <span class="qty-indicator--number">{{ costEstimator.root_assembly_part_cost.exchange_rate }}</span>
            &nbsp;
            <span v-if="costEstimator.bom_version" class="qty-indicator--title">{{ $t('cost.part_cost_form.bom_version') }}</span>
            <span v-if="costEstimator.bom_version" class="qty-indicator--number">{{ costEstimator.bom_version }}</span>
            &nbsp;
            <span class="qty-indicator--title">{{ $t('date') }}</span>
            <span class="qty-indicator--number">{{ moment(costEstimator.created_at).format('YYYY-MM-DD') }}</span>
          </div>
        </div>
        <div data-test-id="analytic-view" id="pie-chart" class="margin-top-20" v-if="chartsData.length > 0">
          <div class="soft-hard-cost">
            <p>{{ $t('cost.costed_bom.soft_hard_cost') }}</p>
            <CostAnalysisChart :data="chartsData" :name-field="softHardFilters"></CostAnalysisChart>
          </div>
          <div class="total-cost-components">
            <p>{{ $t('cost.costed_bom.total_cost_components') }}</p>
            <CostAnalysisChart :data="chartsData" :name-field="totalCostFilters"></CostAnalysisChart>
          </div>
          <div class="recurring-cost">
            <p>{{ $t('cost.costed_bom.recurring') }}</p>
            <CostAnalysisChart :data="chartsData" :name-field="recurringFilters"></CostAnalysisChart>
          </div>
          <div class="total" v-if="costEstimator">
            <p class="flex">
              <span class="title">{{ $t('cost.materials.net_cost') }}:</span>
              <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, net_cost, 2, 2) }}</span>
            </p>
            <p class="flex">
              <span class="title">{{ $t('cost.materials.quality_cost') }}:</span>
              <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, quality_cost, 2, 2) }}</span>
            </p>
            <p class="flex"><span class="title">{{ $t('cost.materials.total') }}:</span> <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, total, 2, 2) }}</span></p>
            <Divider></Divider>
            <p class="flex">
              <span class="title">{{ $t('cost.materials.total_rate') }}:</span>
              <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, total_tax, 2, 2) }}</span>
            </p>
            <p class="flex">
              <span class="title">{{ $t('cost.materials.grand_total') }}:</span>
              <span>{{ intlCurrencyNumber(costEstimator.root_assembly_part_cost.currency, net_cost + quality_cost + total_tax, 2, 2) }}</span>
            </p>
          </div>
        </div>
        <Tabs v-model="selectedTab">
            <TabPane :label="$t('cost.costed_bom.costed_bom_view')" name="costedBomView">
              <PartsLineItems v-if="selectedTab === 'costedBomView' && costEstimator" :part-cost="costEstimator.root_assembly_part_cost"></PartsLineItems>
            </TabPane>
            <TabPane :label="$t('cost.costed_bom.summary_view')" name="summaryView">
              <CostedBomSummary v-if="selectedTab === 'summaryView' && costEstimator" :part-cost="costEstimator.root_assembly_part_cost"></CostedBomSummary>
            </TabPane>
        </Tabs>
      </div>
    </div>
  </div>
</template>

<script>
import { AssembledPartCostInfo, PartsLineItems } from './components'
import { mapActions } from 'vuex'
import CostedBomSummary from './components/forms/costed-bom-summary'
import { intlCurrencyNumber } from '@/libs/util'
import CostAnalysisChart from './components/charts/cost-analysis-chart'
import moment from 'moment'
export default {
  name: 'CostedBom',
  data () {
    return {
      selectedTab: 'costedBomView',
      costEstimator: null,
      chartsData: [],
      net_cost: 0,
      quality_cost: 0,
      risk_cost: 0,
      total: 0,
      total_tax: 0,
      softHardFilters: [
        { report_name: "backend_hard_cost" },
        { report_name: "backend_soft_cost" }
      ],
      totalCostFilters: [
        { report_name: "backend_scrap_cost" },
        { report_name: "backend_engineering_cost" },
        { report_name: "backend_production_cost" },
        { report_name: "express_cost" }
      ],
      recurringFilters: [
        { report_name: "recurring_cost" },
        { report_name: "non_recurring_cost" }
      ]
    }
  },
  components: {
    AssembledPartCostInfo,
    PartsLineItems,
    CostedBomSummary,
    CostAnalysisChart
  },
  computed: {
    pageTitle: function () {
      document.title = this.$t('menu.estimators.assembled_part_costed_bom') + ' - ' + this.costEstimator?.manufacturer_part?.display_name
      return document.title
    }
  },
  mounted () {
    this.loadCostEstimatorInfo(this.$route.params.id)
  },
  watch: {
    '$route.params.id': function(newVal) {
      if (newVal) {
        this.loadCostEstimatorInfo(newVal)
      }
    },
    costEstimator: {
      handler (newVal) {
        if (newVal) {
          this.loadChartsData(newVal.root_assembly_part_cost.cost_estimator_id)
        }
      },
      deep: true
    }
  },
  methods: {
    ...mapActions([
      'getCostEstimatorInfo',
      'getCostedBOMReport'
    ]),
    moment,
    intlCurrencyNumber,
    loadCostEstimatorInfo(id) {
      this.getCostEstimatorInfo(id).then(res => {
        if (res.status === 200) {
          this.costEstimator = res.data
        }
      })
    },
    handleToMaterial () {
      this.$router.push({
        name: 'menu.estimators.purchased_parts_cost',
        params: { id: this.costEstimator.id }
      })
    },
    loadChartsData(id) {
      let reports = [
        'backend_hard_cost',
        'backend_soft_cost',
        'backend_scrap_cost',
        'backend_engineering_cost',
        'backend_production_cost',
        'express_cost',
        'backend_net_cost',
        'backend_total_tax',
        'backend_total_cost',
        'recurring_cost',
        'non_recurring_cost'
      ]
      this.getCostedBOMReport({ costEstimatorId: id, reports }).then(res => {
        this.net_cost = _.find(res.data.reports, { report_name: "backend_net_cost" }).total_cost
        this.quality_cost = _.find(res.data.reports, { report_name: "backend_scrap_cost" }).total_cost
        // this.risk_cost = _.find(res.data.reports, { report_name: "backend_risk_cost" }).total_cost
        this.total = _.find(res.data.reports, { report_name: "backend_total_cost" }).total_cost
        this.total_tax = _.find(res.data.reports, { report_name: "backend_total_tax" }).total_cost
        this.chartsData = res.data.reports
      })
    }
  }
}
</script>

<style lang="less">
#costed-bom {
  .qty-indicator {
    margin-bottom: 55px;
    .qty-indicator--number, .qty-indicator--title {
      display: inline-block;
      min-width: 50px;
      border: 1px solid #cccccc4d;
      padding: 1px 5px;
      background: #cccccc3d;
      border-radius: 0 4px 4px 0;
      color: #999;
      text-align: center;
      font-weight: bold;
    }
    .qty-indicator--title {
      border-radius: 4px 0 0 4px;
      text-align: right;
      border-right: 0;
    }
  }
  #pie-chart {
    display: flex;
    align-items: center;
    .soft-hard-cost, .total-cost-components, .recurring-cost {
      text-align: center;
    }
    .total {
      margin-top: 20px;
      width: 200px;
      .flex {
        display: flex;
        justify-content: space-between;
        align-items: center;
      }
      .title {
        font-size: 16px;
        font-weight: bold;
      }
    }
  }
}
</style>
